import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import AdminLayout from './AdminLayout';
import { supabase, getUserById } from './supabaseClient';
import './Payments.css';

const Payments = () => {
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState('');
  const [paymentStatusFilter, setPaymentStatusFilter] = useState('all');
  const [paymentMethodFilter, setPaymentMethodFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [allPayments, setAllPayments] = useState([]);
  const [summaryStats, setSummaryStats] = useState({
    totalRevenue: '₦0.00',
    successfulPayments: 0,
    failedPayments: 0,
    refundRequests: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showDetailsModal, setShowDetailsModal] = useState(false);

  // Fetch real payments data from Supabase
  useEffect(() => {
    const fetchPayments = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch all payments from booking_payments table
        const { data: paymentsData, error: paymentsError } = await supabase
          .from('booking_payments')
          .select(`
            *,
            flight_bookings:booking_id (
              id,
              booking_reference,
              user_id,
              total_amount
            )
          `)
          .order('payment_date', { ascending: false });

        if (paymentsError) throw paymentsError;

        // Fetch user information for each payment
        const paymentsWithDetails = await Promise.all(
          paymentsData.map(async (payment) => {
            let userName = 'Unknown User';
            let bookingReference = 'N/A';

            // Get booking reference
            if (payment.flight_bookings) {
              bookingReference = payment.flight_bookings.booking_reference || 'N/A';
            }

            // Get user name
            const userId = payment.user_id || (payment.flight_bookings?.user_id);
            if (userId) {
              try {
                const user = await getUserById(userId);
                if (user) {
                  userName = user.name || user.email || 'Unknown User';
                }
              } catch (err) {
                console.warn('Could not fetch user for payment:', payment.id, err);
              }
            }

            // Format transaction ID
            const transactionId = payment.transaction_id || 
                                 payment.payment_reference || 
                                 payment.id.substring(0, 8).toUpperCase();

            // Format amount
            const amount = payment.amount
              ? `₦${parseFloat(payment.amount).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
              : '₦0.00';

            // Format payment date
            const paymentDate = payment.payment_date
              ? new Date(payment.payment_date).toLocaleDateString('en-NG', { 
                  year: 'numeric', 
                  month: '2-digit', 
                  day: '2-digit' 
                })
              : 'N/A';

            // Map payment status
            let status = 'Pending';
            if (payment.payment_status === 'completed') status = 'Success';
            else if (payment.payment_status === 'failed') status = 'Failed';
            else if (payment.payment_status === 'refunded') status = 'Refunded';
            else if (payment.payment_status === 'processing') status = 'Processing';
            else if (payment.payment_status === 'cancelled') status = 'Cancelled';
            else status = 'Pending';

            // Map payment method
            let method = payment.payment_method || 'N/A';
            if (method === 'bank_transfer') method = 'Bank Transfer';
            else if (method === 'paystack') method = 'Paystack';
            else if (method === 'stripe') method = 'Stripe';
            else if (method === 'flutterwave') method = 'Flutterwave';
            else if (method === 'ussd') method = 'USSD';
            else method = method.charAt(0).toUpperCase() + method.slice(1);

            return {
              id: transactionId,
              paymentId: payment.id,
              bookingId: bookingReference,
              bookingUuid: payment.booking_id,
              userId: userId,
              userName: userName,
              method: method,
              amount: amount,
              rawAmount: parseFloat(payment.amount || 0),
              date: paymentDate,
              status: status,
              paymentStatus: payment.payment_status,
              currency: payment.currency || 'NGN',
              paymentReference: payment.payment_reference,
              gatewayTransactionId: payment.gateway_transaction_id,
              refundAmount: payment.refund_amount || 0,
              refundDate: payment.refund_date,
              rawPayment: payment // Keep raw data for details
            };
          })
        );

        setAllPayments(paymentsWithDetails);

        // Calculate summary statistics
        const totalRevenue = paymentsWithDetails
          .filter(p => p.status === 'Success')
          .reduce((sum, p) => sum + p.rawAmount, 0);
        
        const successfulPayments = paymentsWithDetails.filter(p => p.status === 'Success').length;
        const failedPayments = paymentsWithDetails.filter(p => p.status === 'Failed').length;
        const refundRequests = paymentsWithDetails.filter(p => p.status === 'Refunded' || p.refundAmount > 0).length;

        setSummaryStats({
          totalRevenue: `₦${totalRevenue.toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`,
          successfulPayments,
          failedPayments,
          refundRequests
        });
      } catch (err) {
        console.error('Error fetching payments:', err);
        setError(err.message || 'Failed to fetch payments');
      } finally {
        setLoading(false);
      }
    };

    fetchPayments();
  }, []);

  // Placeholder payments data (fallback if no data)
  const placeholderPayments = [
    {
      id: 'TXN001234',
      bookingId: 'SF12345678',
      userId: 'USR001',
      userName: 'John Doe',
      method: 'Paystack',
      amount: '₦850,000',
      date: '2024-12-10',
      status: 'Success'
    },
    {
      id: 'TXN001235',
      bookingId: 'SF12345679',
      userId: 'USR002',
      userName: 'Jane Smith',
      method: 'Bank Transfer',
      amount: '₦720,000',
      date: '2024-12-11',
      status: 'Pending'
    },
    {
      id: 'TXN001236',
      bookingId: 'SF12345680',
      userId: 'USR003',
      userName: 'Michael Brown',
      method: 'Paystack',
      amount: '₦1,250,000',
      date: '2024-12-09',
      status: 'Success'
    },
    {
      id: 'TXN001237',
      bookingId: 'SF12345681',
      userId: 'USR004',
      userName: 'Sarah Johnson',
      method: 'USSD',
      amount: '₦45,000',
      date: '2024-12-08',
      status: 'Failed'
    },
    {
      id: 'TXN001238',
      bookingId: 'SF12345682',
      userId: 'USR005',
      userName: 'David Williams',
      method: 'Paystack',
      amount: '₦680,000',
      date: '2024-12-12',
      status: 'Success'
    },
    {
      id: 'TXN001239',
      bookingId: 'SF12345683',
      userId: 'USR006',
      userName: 'Emily Davis',
      method: 'Bank Transfer',
      amount: '₦920,000',
      date: '2024-12-13',
      status: 'Pending'
    },
    {
      id: 'TXN001240',
      bookingId: 'SF12345684',
      userId: 'USR007',
      userName: 'Robert Miller',
      method: 'Paystack',
      amount: '₦38,000',
      date: '2024-12-07',
      status: 'Success'
    },
    {
      id: 'TXN001241',
      bookingId: 'SF12345685',
      userId: 'USR008',
      userName: 'Lisa Anderson',
      method: 'Paystack',
      amount: '₦750,000',
      date: '2024-12-14',
      status: 'Failed'
    },
    {
      id: 'TXN001242',
      bookingId: 'SF12345686',
      userId: 'USR009',
      userName: 'James Wilson',
      method: 'Bank Transfer',
      amount: '₦1,100,000',
      date: '2024-12-06',
      status: 'Success'
    },
    {
      id: 'TXN001243',
      bookingId: 'SF12345687',
      userId: 'USR010',
      userName: 'Maria Garcia',
      method: 'USSD',
      amount: '₦890,000',
      date: '2024-12-15',
      status: 'Success'
    },
    {
      id: 'TXN001244',
      bookingId: 'SF12345688',
      userId: 'USR011',
      userName: 'William Martinez',
      method: 'Paystack',
      amount: '₦650,000',
      date: '2024-12-05',
      status: 'Success'
    },
    {
      id: 'TXN001245',
      bookingId: 'SF12345689',
      userId: 'USR012',
      userName: 'Patricia Taylor',
      method: 'Paystack',
      amount: '₦42,000',
      date: '2024-12-04',
      status: 'Failed'
    }
  ];

  // Use real data if available, otherwise use placeholder (for development)
  const paymentsToDisplay = allPayments.length > 0 ? allPayments : placeholderPayments;

  // Filter payments based on search and filters
  const filteredPayments = paymentsToDisplay.filter(payment => {
    const matchesSearch = 
      payment.id.toLowerCase().includes(searchQuery.toLowerCase()) ||
      payment.bookingId.toLowerCase().includes(searchQuery.toLowerCase());
    
    const matchesPaymentStatus = paymentStatusFilter === 'all' || 
      payment.status.toLowerCase() === paymentStatusFilter.toLowerCase() ||
      (paymentStatusFilter === 'success' && payment.status.toLowerCase() === 'completed');
    
    const matchesPaymentMethod = paymentMethodFilter === 'all' || 
      payment.method.toLowerCase().replace(' ', '-').replace('_', '-') === paymentMethodFilter.toLowerCase();
    
    return matchesSearch && matchesPaymentStatus && matchesPaymentMethod;
  });

  // Pagination calculations
  const totalPages = Math.ceil(filteredPayments.length / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = startIndex + rowsPerPage;
  const paginatedPayments = filteredPayments.slice(startIndex, endIndex);

  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    setCurrentPage(1);
  };

  const handlePaymentStatusFilterChange = (e) => {
    setPaymentStatusFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePaymentMethodFilterChange = (e) => {
    setPaymentMethodFilter(e.target.value);
    setCurrentPage(1);
  };

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (e) => {
    setRowsPerPage(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleExport = () => {
    // Placeholder for export functionality
    console.log('Exporting payments to CSV...');
  };

  const handleViewDetails = (payment) => {
    setSelectedPayment(payment);
    setShowDetailsModal(true);
  };

  const handleCloseModal = () => {
    setShowDetailsModal(false);
    setSelectedPayment(null);
  };

  return (
    <AdminLayout pageTitle="Payments">
      <div className="admin-payments-page">
        {/* Page Title */}
        <div className="admin-payments-header">
          <h1 className="admin-payments-title">Payments</h1>
          <p className="admin-payments-subtitle">View and manage transactions</p>
        </div>

        {/* Top Summary Cards */}
        <div className="admin-payments-summary">
          <div className="admin-summary-card">
            <div className="admin-summary-icon">💰</div>
            <div className="admin-summary-content">
              <div className="admin-summary-label">Total Revenue</div>
              <div className="admin-summary-value">{summaryStats.totalRevenue}</div>
            </div>
          </div>
          <div className="admin-summary-card">
            <div className="admin-summary-icon">✅</div>
            <div className="admin-summary-content">
              <div className="admin-summary-label">Successful Payments</div>
              <div className="admin-summary-value">{summaryStats.successfulPayments}</div>
            </div>
          </div>
          <div className="admin-summary-card">
            <div className="admin-summary-icon">❌</div>
            <div className="admin-summary-content">
              <div className="admin-summary-label">Failed Payments</div>
              <div className="admin-summary-value">{summaryStats.failedPayments}</div>
            </div>
          </div>
          <div className="admin-summary-card">
            <div className="admin-summary-icon">↩️</div>
            <div className="admin-summary-content">
              <div className="admin-summary-label">Refund Requests</div>
              <div className="admin-summary-value">{summaryStats.refundRequests}</div>
            </div>
          </div>
        </div>

        {/* Top Control Bar */}
        <div className="admin-payments-controls">
          <div className="admin-controls-left">
            <div className="admin-search-wrapper">
              <input
                type="text"
                className="admin-search-input"
                placeholder="Search by Transaction ID or Booking ID..."
                value={searchQuery}
                onChange={handleSearchChange}
              />
              <span className="admin-search-icon">🔍</span>
            </div>
            
            <select
              className="admin-filter-select"
              value={paymentStatusFilter}
              onChange={handlePaymentStatusFilterChange}
            >
              <option value="all">All Payment Status</option>
              <option value="success">Success</option>
              <option value="completed">Completed</option>
              <option value="failed">Failed</option>
              <option value="pending">Pending</option>
              <option value="processing">Processing</option>
              <option value="refunded">Refunded</option>
              <option value="cancelled">Cancelled</option>
            </select>

            <select
              className="admin-filter-select"
              value={paymentMethodFilter}
              onChange={handlePaymentMethodFilterChange}
            >
              <option value="all">All Payment Methods</option>
              <option value="paystack">Paystack</option>
              <option value="stripe">Stripe</option>
              <option value="flutterwave">Flutterwave</option>
              <option value="bank-transfer">Bank Transfer</option>
              <option value="ussd">USSD</option>
              <option value="cash">Cash</option>
            </select>

            <select className="admin-filter-select">
              <option value="">Date Range</option>
              <option value="today">Today</option>
              <option value="week">This Week</option>
              <option value="month">This Month</option>
            </select>
          </div>

          <div className="admin-controls-right">
            <button className="admin-export-button" onClick={handleExport}>
              <span className="admin-export-icon">📥</span>
              Export CSV
            </button>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="admin-empty-state">
            <div className="admin-empty-icon">⏳</div>
            <h3 className="admin-empty-title">Loading payments...</h3>
            <p className="admin-empty-text">Please wait while we fetch your data</p>
          </div>
        )}

        {/* Error State */}
        {error && !loading && (
          <div className="admin-empty-state">
            <div className="admin-empty-icon">⚠️</div>
            <h3 className="admin-empty-title">Error loading payments</h3>
            <p className="admin-empty-text">{error}</p>
            <button 
              className="admin-action-button"
              onClick={() => window.location.reload()}
              style={{ marginTop: '1rem' }}
            >
              Retry
            </button>
          </div>
        )}

        {/* Payments Table */}
        {!loading && !error && paginatedPayments.length > 0 ? (
          <>
            <div className="admin-payments-table-wrapper">
              {/* Desktop Table */}
              <table className="admin-payments-table">
                <thead>
                  <tr>
                    <th>Transaction ID</th>
                    <th>Booking ID</th>
                    <th>User</th>
                    <th>Payment Method</th>
                    <th>Amount</th>
                    <th>Date</th>
                    <th>Status</th>
                    <th>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {paginatedPayments.map((payment) => (
                    <tr key={payment.id} className="admin-payments-table-row">
                      <td className="admin-table-cell admin-table-id">{payment.id}</td>
                      <td className="admin-table-cell admin-table-id">{payment.bookingId}</td>
                      <td className="admin-table-cell">{payment.userName}</td>
                      <td className="admin-table-cell">{payment.method}</td>
                      <td className="admin-table-cell admin-table-amount">{payment.amount}</td>
                      <td className="admin-table-cell">{payment.date}</td>
                      <td className="admin-table-cell">
                        <span className={`admin-status-badge admin-status-${payment.status.toLowerCase()}`}>
                          {payment.status}
                        </span>
                      </td>
                      <td className="admin-table-cell">
                        <div className="admin-actions-menu">
                          <button
                            className="admin-action-button"
                            onClick={() => handleViewDetails(payment)}
                            style={{
                              padding: '8px 12px',
                              backgroundColor: '#007bff',
                              color: 'white',
                              border: 'none',
                              borderRadius: '4px',
                              cursor: 'pointer',
                              fontSize: '14px'
                            }}
                          >
                            View Details
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              {/* Mobile Card View */}
              <div className="admin-payments-cards">
                {paginatedPayments.map((payment) => (
                  <div key={payment.id} className="admin-payment-card">
                    <div className="admin-payment-card-header">
                      <div className="admin-payment-card-id">{payment.id}</div>
                      <span className={`admin-status-badge admin-status-${payment.status.toLowerCase()}`}>
                        {payment.status}
                      </span>
                    </div>
                    <div className="admin-payment-card-body">
                      <div className="admin-payment-card-row">
                        <span className="admin-payment-card-label">Booking ID:</span>
                        <span className="admin-payment-card-value admin-payment-card-id">{payment.bookingId}</span>
                      </div>
                      <div className="admin-payment-card-row">
                        <span className="admin-payment-card-label">User:</span>
                        <span className="admin-payment-card-value">{payment.userName}</span>
                      </div>
                      <div className="admin-payment-card-row">
                        <span className="admin-payment-card-label">Method:</span>
                        <span className="admin-payment-card-value">{payment.method}</span>
                      </div>
                      <div className="admin-payment-card-row">
                        <span className="admin-payment-card-label">Amount:</span>
                        <span className="admin-payment-card-value admin-payment-card-amount">{payment.amount}</span>
                      </div>
                      <div className="admin-payment-card-row">
                        <span className="admin-payment-card-label">Date:</span>
                        <span className="admin-payment-card-value">{payment.date}</span>
                      </div>
                    </div>
                    <div className="admin-payment-card-actions">
                      <button
                        className="admin-action-button"
                        onClick={() => handleViewDetails(payment)}
                        style={{
                          width: '100%',
                          padding: '10px',
                          backgroundColor: '#007bff',
                          color: 'white',
                          border: 'none',
                          borderRadius: '4px',
                          cursor: 'pointer',
                          fontSize: '14px'
                        }}
                      >
                        View Details
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Pagination */}
            <div className="admin-pagination">
              <div className="admin-pagination-info">
                Showing {startIndex + 1} to {Math.min(endIndex, filteredPayments.length)} of {filteredPayments.length} payments
              </div>
              <div className="admin-pagination-controls">
                <select
                  className="admin-rows-select"
                  value={rowsPerPage}
                  onChange={handleRowsPerPageChange}
                >
                  <option value={10}>10 per page</option>
                  <option value={25}>25 per page</option>
                  <option value={50}>50 per page</option>
                  <option value={100}>100 per page</option>
                </select>
                <div className="admin-page-numbers">
                  <button
                    className="admin-page-button"
                    onClick={() => handlePageChange(currentPage - 1)}
                    disabled={currentPage === 1}
                  >
                    Previous
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1)
                    .filter(page => {
                      return page === 1 || 
                             page === totalPages || 
                             (page >= currentPage - 1 && page <= currentPage + 1);
                    })
                    .map((page, index, array) => {
                      const prevPage = array[index - 1];
                      const showEllipsis = prevPage && page - prevPage > 1;
                      
                      return (
                        <React.Fragment key={page}>
                          {showEllipsis && <span className="admin-page-ellipsis">...</span>}
                          <button
                            className={`admin-page-button ${currentPage === page ? 'active' : ''}`}
                            onClick={() => handlePageChange(page)}
                          >
                            {page}
                          </button>
                        </React.Fragment>
                      );
                    })}
                  <button
                    className="admin-page-button"
                    onClick={() => handlePageChange(currentPage + 1)}
                    disabled={currentPage === totalPages}
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          </>
        ) : !loading && !error ? (
          /* Empty State */
          <div className="admin-empty-state">
            <div className="admin-empty-icon">💳</div>
            <h3 className="admin-empty-title">No transactions found</h3>
            <p className="admin-empty-text">
              {searchQuery || paymentStatusFilter !== 'all' || paymentMethodFilter !== 'all'
                ? 'Try adjusting your search or filters' 
                : 'No payment transactions have been recorded yet'}
            </p>
          </div>
        ) : null}

        {/* Payment Details Modal/Popup */}
        {showDetailsModal && selectedPayment && (
          <div 
            style={{
              position: 'fixed',
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              backgroundColor: 'rgba(0, 0, 0, 0.6)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              zIndex: 1000,
              padding: '20px',
              backdropFilter: 'blur(4px)'
            }}
            onClick={handleCloseModal}
          >
            <div 
              style={{
                backgroundColor: '#ffffff',
                borderRadius: '16px',
                padding: 0,
                maxWidth: '900px',
                width: '100%',
                maxHeight: '90vh',
                overflow: 'hidden',
                boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
                position: 'relative',
                display: 'flex',
                flexDirection: 'column'
              }}
              onClick={(e) => e.stopPropagation()}
            >
              {/* Header with gradient */}
              <div style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                padding: '24px 30px',
                color: 'white',
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                borderRadius: '16px 16px 0 0'
              }}>
                <div>
                  <h2 style={{ margin: 0, fontSize: '24px', fontWeight: '600', color: 'white' }}>
                    Payment Details
                  </h2>
                  <p style={{ margin: '4px 0 0 0', fontSize: '14px', opacity: 0.9 }}>
                    Transaction ID: {selectedPayment.id}
                  </p>
                </div>
                <button
                  onClick={handleCloseModal}
                  style={{
                    background: 'rgba(255, 255, 255, 0.2)',
                    border: 'none',
                    borderRadius: '50%',
                    width: '36px',
                    height: '36px',
                    fontSize: '20px',
                    cursor: 'pointer',
                    color: 'white',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    transition: 'background 0.2s'
                  }}
                  onMouseEnter={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.3)'}
                  onMouseLeave={(e) => e.target.style.background = 'rgba(255, 255, 255, 0.2)'}
                >
                  ×
                </button>
              </div>

              {/* Content Area */}
              <div style={{
                padding: '30px',
                overflowY: 'auto',
                flex: 1,
                backgroundColor: '#f8f9fa'
              }}>
                <div style={{ display: 'grid', gap: '20px' }}>
                  {/* Transaction Information Card */}
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    border: '1px solid #e9ecef'
                  }}>
                    <h3 style={{
                      margin: '0 0 16px 0',
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{ fontSize: '20px' }}>💳</span>
                      Transaction Information
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                      <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Transaction ID</div>
                        <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>{selectedPayment.id}</div>
                      </div>
                      <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Payment Reference</div>
                        <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>{selectedPayment.paymentReference || 'N/A'}</div>
                      </div>
                      <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Gateway Transaction ID</div>
                        <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>{selectedPayment.gatewayTransactionId || 'N/A'}</div>
                      </div>
                      <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Status</div>
                        <div>
                          <span className={`admin-status-badge admin-status-${selectedPayment.status.toLowerCase()}`}>
                            {selectedPayment.status}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Booking Information Card */}
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    border: '1px solid #e9ecef'
                  }}>
                    <h3 style={{
                      margin: '0 0 16px 0',
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{ fontSize: '20px' }}>🎫</span>
                      Booking Information
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                      <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Booking ID</div>
                        <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>{selectedPayment.bookingId}</div>
                      </div>
                      <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>User Name</div>
                        <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>{selectedPayment.userName}</div>
                      </div>
                      <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>User ID</div>
                        <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>
                          {selectedPayment.userId ? selectedPayment.userId.substring(0, 8).toUpperCase() : 'N/A'}
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Payment Details Card */}
                  <div style={{
                    backgroundColor: 'white',
                    borderRadius: '12px',
                    padding: '20px',
                    boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                    border: '1px solid #e9ecef'
                  }}>
                    <h3 style={{
                      margin: '0 0 16px 0',
                      fontSize: '18px',
                      fontWeight: '600',
                      color: '#333',
                      display: 'flex',
                      alignItems: 'center',
                      gap: '8px'
                    }}>
                      <span style={{ fontSize: '20px' }}>💰</span>
                      Payment Details
                    </h3>
                    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                      <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Payment Method</div>
                        <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>{selectedPayment.method}</div>
                      </div>
                      <div style={{ padding: '12px', backgroundColor: '#e7f5ff', borderRadius: '8px', border: '2px solid #339af0' }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Amount</div>
                        <div style={{ fontSize: '18px', color: '#339af0', fontWeight: '700' }}>{selectedPayment.amount}</div>
                      </div>
                      <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Currency</div>
                        <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>{selectedPayment.currency}</div>
                      </div>
                      <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                        <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Payment Date</div>
                        <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>{selectedPayment.date}</div>
                      </div>
                      {selectedPayment.rawPayment?.payment_date && (
                        <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                          <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Full Payment Date</div>
                          <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>
                            {new Date(selectedPayment.rawPayment.payment_date).toLocaleString('en-NG')}
                          </div>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Refund Information Card (if applicable) */}
                  {(selectedPayment.refundAmount > 0 || selectedPayment.status === 'Refunded') && (
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      padding: '20px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      border: '1px solid #e9ecef'
                    }}>
                      <h3 style={{
                        margin: '0 0 16px 0',
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#333',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ fontSize: '20px' }}>↩️</span>
                        Refund Information
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                        <div style={{ padding: '12px', backgroundColor: '#fff3cd', borderRadius: '8px', border: '2px solid #ffc107' }}>
                          <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Refund Amount</div>
                          <div style={{ fontSize: '18px', color: '#856404', fontWeight: '700' }}>
                            ₦{parseFloat(selectedPayment.refundAmount || 0).toLocaleString('en-NG', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                        </div>
                        {selectedPayment.refundDate && (
                          <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                            <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Refund Date</div>
                            <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>
                              {new Date(selectedPayment.refundDate).toLocaleDateString('en-NG')}
                            </div>
                          </div>
                        )}
                        {selectedPayment.rawPayment?.refund_reference && (
                          <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                            <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Refund Reference</div>
                            <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>{selectedPayment.rawPayment.refund_reference}</div>
                          </div>
                        )}
                        {selectedPayment.rawPayment?.refund_reason && (
                          <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px', gridColumn: '1 / -1' }}>
                            <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Refund Reason</div>
                            <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>{selectedPayment.rawPayment.refund_reason}</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* Additional Information Card */}
                  {selectedPayment.rawPayment && (
                    <div style={{
                      backgroundColor: 'white',
                      borderRadius: '12px',
                      padding: '20px',
                      boxShadow: '0 2px 8px rgba(0, 0, 0, 0.08)',
                      border: '1px solid #e9ecef'
                    }}>
                      <h3 style={{
                        margin: '0 0 16px 0',
                        fontSize: '18px',
                        fontWeight: '600',
                        color: '#333',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px'
                      }}>
                        <span style={{ fontSize: '20px' }}>ℹ️</span>
                        Additional Information
                      </h3>
                      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))', gap: '16px' }}>
                        {selectedPayment.rawPayment.exchange_rate && selectedPayment.rawPayment.exchange_rate !== 1.0 && (
                          <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                            <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Exchange Rate</div>
                            <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>{selectedPayment.rawPayment.exchange_rate}</div>
                          </div>
                        )}
                        <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                          <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Created At</div>
                          <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>
                            {selectedPayment.rawPayment.created_at ? new Date(selectedPayment.rawPayment.created_at).toLocaleString('en-NG') : 'N/A'}
                          </div>
                        </div>
                        <div style={{ padding: '12px', backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                          <div style={{ fontSize: '12px', color: '#6c757d', marginBottom: '4px', fontWeight: '500' }}>Updated At</div>
                          <div style={{ fontSize: '15px', color: '#212529', fontWeight: '500' }}>
                            {selectedPayment.rawPayment.updated_at ? new Date(selectedPayment.rawPayment.updated_at).toLocaleString('en-NG') : 'N/A'}
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>

              {/* Footer */}
              <div style={{
                padding: '20px 30px',
                backgroundColor: '#f8f9fa',
                borderTop: '1px solid #e9ecef',
                display: 'flex',
                justifyContent: 'flex-end',
                borderRadius: '0 0 16px 16px'
              }}>
                <button
                  onClick={handleCloseModal}
                  style={{
                    padding: '12px 32px',
                    backgroundColor: '#667eea',
                    color: 'white',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: 'pointer',
                    fontSize: '15px',
                    fontWeight: '500',
                    transition: 'all 0.2s',
                    boxShadow: '0 2px 8px rgba(102, 126, 234, 0.3)'
                  }}
                  onMouseEnter={(e) => {
                    e.target.style.backgroundColor = '#5568d3';
                    e.target.style.transform = 'translateY(-1px)';
                    e.target.style.boxShadow = '0 4px 12px rgba(102, 126, 234, 0.4)';
                  }}
                  onMouseLeave={(e) => {
                    e.target.style.backgroundColor = '#667eea';
                    e.target.style.transform = 'translateY(0)';
                    e.target.style.boxShadow = '0 2px 8px rgba(102, 126, 234, 0.3)';
                  }}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default Payments;

